import React from 'react';

/** Tiny safe markdown renderer. Produces React elements only; raw HTML in the input is shown as text. */

type Block =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered: boolean; items: Array<{ depth: number; text: string }> }
  | { type: 'quote'; lines: string[] }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'rule' }
  | { type: 'code'; text: string };

const BULLET = /^(\s*)[-*•]\s+(.*)$/u;
const NUMBERED = /^(\s*)\d+[.)]\s+(.*)$/u;
const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/u;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/u;

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/u, '')
    .replace(/\|$/u, '')
    .split('|')
    .map((cell) => cell.trim());
}

function isBlockStart(line: string): boolean {
  return (
    HEADING.test(line) ||
    BULLET.test(line) ||
    NUMBERED.test(line) ||
    /^\s*>/u.test(line) ||
    /^\s*```/u.test(line) ||
    /^\s*([-*_])(\s*\1){2,}\s*$/u.test(line)
  );
}

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n?/gu, '\n').split('\n');
  const blocks: Block[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index] ?? '';
    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (/^\s*```/u.test(line)) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !/^\s*```/u.test(lines[index] ?? '')) {
        code.push(lines[index] ?? '');
        index += 1;
      }
      index += 1;
      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }

    if (/^\s*([-*_])(\s*\1){2,}\s*$/u.test(line)) {
      blocks.push({ type: 'rule' });
      index += 1;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ type: 'heading', level: (heading[1] ?? '#').length, text: heading[2] ?? '' });
      index += 1;
      continue;
    }

    if (line.includes('|') && TABLE_SEPARATOR.test(lines[index + 1] ?? '')) {
      const header = splitRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && (lines[index] ?? '').includes('|') && (lines[index] ?? '').trim()) {
        rows.push(splitRow(lines[index] ?? ''));
        index += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    if (/^\s*>/u.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^\s*>/u.test(lines[index] ?? '')) {
        quote.push((lines[index] ?? '').replace(/^\s*>\s?/u, ''));
        index += 1;
      }
      blocks.push({ type: 'quote', lines: quote });
      continue;
    }

    const listMatch = BULLET.exec(line) ?? NUMBERED.exec(line);
    if (listMatch) {
      const ordered = NUMBERED.test(line) && !BULLET.test(line);
      const items: Array<{ depth: number; text: string }> = [];
      while (index < lines.length) {
        const current = lines[index] ?? '';
        const match = (ordered ? NUMBERED : BULLET).exec(current);
        if (match) {
          items.push({ depth: Math.min(3, Math.floor((match[1] ?? '').replace(/\t/gu, '  ').length / 2)), text: match[2] ?? '' });
          index += 1;
        } else if (current.trim() && /^\s{2,}\S/u.test(current) && items.length && !isBlockStart(current)) {
          const last = items[items.length - 1];
          if (last) last.text += ` ${current.trim()}`;
          index += 1;
        } else {
          break;
        }
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length && (lines[index] ?? '').trim() && (paragraph.length === 0 || !isBlockStart(lines[index] ?? ''))) {
      paragraph.push((lines[index] ?? '').trim());
      index += 1;
    }
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
  }
  return blocks;
}

const INLINE = /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\*[^*\s][^*]*\*|\[[^\]]+\]\(https?:\/\/[^\s)]+\)|\[\d{1,3}\](?!\())/gu;

export interface Citation {
  label: string;
  url: string;
}

function renderInline(text: string, keyPrefix: string, citations: ReadonlyArray<Citation>): React.ReactNode[] {
  return text.split(INLINE).map((part, position) => {
    const key = `${keyPrefix}-${position}`;
    if (!part) return null;
    if (/^\*\*[^*]+\*\*$/u.test(part) || /^__[^_]+__$/u.test(part)) {
      return <strong key={key}>{renderInline(part.slice(2, -2), key, citations)}</strong>;
    }
    if (/^`[^`]+`$/u.test(part)) return <code key={key}>{part.slice(1, -1)}</code>;
    if (/^\*[^*\s][^*]*\*$/u.test(part)) return <em key={key}>{renderInline(part.slice(1, -1), key, citations)}</em>;
    const cite = /^\[(\d{1,3})\]$/u.exec(part);
    if (cite) {
      const source = citations[Number(cite[1]) - 1];
      if (!source) return <React.Fragment key={key}>{part}</React.Fragment>;
      return (
        <sup className="md-cite" key={key}>
          {source.url ? (
            <a href={source.url} rel="noopener noreferrer" target="_blank" title={source.label}>[{cite[1]}]</a>
          ) : (
            <span title={source.label}>[{cite[1]}]</span>
          )}
        </sup>
      );
    }
    const link = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/u.exec(part);
    if (link) {
      return (
        <a href={link[2]} key={key} rel="noopener noreferrer" target="_blank">
          {link[1]}
        </a>
      );
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

export function Markdown({ citations = [], className = '', source }: { citations?: ReadonlyArray<Citation>; className?: string; source: string }) {
  const blocks = React.useMemo(() => parseMarkdown(source), [source]);
  return (
    <div className={`md ${className}`.trim()}>
      {blocks.map((block, position) => {
        const key = `b${position}`;
        switch (block.type) {
          case 'heading': {
            const level = Math.min(6, block.level + 1);
            return React.createElement(`h${level}`, { key }, renderInline(block.text, key, citations));
          }
          case 'paragraph':
            return <p key={key}>{renderInline(block.text, key, citations)}</p>;
          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
              <Tag key={key}>
                {block.items.map((item, itemPosition) => (
                  <li className={`md-depth-${item.depth}`} key={itemPosition}>
                    {renderInline(item.text, `${key}-${itemPosition}`, citations)}
                  </li>
                ))}
              </Tag>
            );
          }
          case 'quote':
            return <blockquote key={key}>{renderInline(block.lines.join(' '), key, citations)}</blockquote>;
          case 'table':
            return (
              <div className="md-table-wrap" key={key}>
                <table>
                  <thead>
                    <tr>
                      {block.header.map((cell, cellPosition) => (
                        <th key={cellPosition}>{renderInline(cell, `${key}-h${cellPosition}`, citations)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, rowPosition) => (
                      <tr key={rowPosition}>
                        {row.map((cell, cellPosition) => (
                          <td key={cellPosition}>{renderInline(cell, `${key}-${rowPosition}-${cellPosition}`, citations)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'rule':
            return <hr key={key} />;
          case 'code':
            return <pre key={key}><code>{block.text}</code></pre>;
        }
      })}
    </div>
  );
}
